import React from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import ChevronRight from 'common/Icons/ChevronRight';
import { AICompetitiveIntelligenceType } from 'types/recommendations';

import CategoryRankList from './CategoryRankList';
import CompetitiveAdvantagesImprovements from './CompetitiveAdvantagesImprovements';

const Container = styled.div`
  border-radius: 10px;
  border: solid 1px var(--line-gray);
  padding: 30px;
  padding-bottom: 0;
  background-color: var(--white);
`;

const Header = styled.div`
  display: flex;
  flex-flow: row;
  align-items: flex-start;
  justify-content: space-between;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--line-gray);
`;

const TitleGroup = styled.div`
  display: flex;
  flex-flow: row;
  gap: 10px;
  align-items: center;
`;

const ImageContainer = styled.div`
  background-image: linear-gradient(to bottom, var(--lavender-frost), var(--lavender-frost-end));
  border-radius: 4px;
  height: 32px;
  width: 32px;
  margin-top: -4px;
`;

const Title = styled.div`
  font-size: 20px;
  font-family: var(--font-bold);
`;

const Subtitle = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
`;

const AICIButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  border-radius: 4px;
  background: linear-gradient(
    277deg,
    var(--competitor-purple),
    var(--aici-action-icon-gradient-mid) 50%,
    var(--flamingo-pink)
  );
  color: var(--white);
  font-family: var(--font-bold);
  font-size: 14px;
  padding: 10px 20px;
  cursor: pointer;
`;

const Body = styled.div`
  display: flex;
  flex-flow: row;
  gap: 30px;
`;

type Props = {
  data?: AICompetitiveIntelligenceType;
  communityId?: number;
  competingCommunitiesCount?: number;
};

const CategoryBreakdownCard = ({ data, communityId, competingCommunitiesCount }: Props) => {
  const navigate = useNavigate();

  const categories = data?.related_data ?? [];
  const strengths = data?.strengths ?? [];
  const limitations = data?.limitations ?? [];

  const handleOpenAICompetitiveIntelligence = () => {
    navigate(communityId ? `/ai-competitive-intelligence?communityId=${communityId}` : '/ai-competitive-intelligence');
  };

  return (
    <Container data-test-id="category-breakdown-card">
      <Header>
        <TitleGroup>
          <ImageContainer>
            <img src="/icons/bars-blue.svg" width="32" height="32" />
          </ImageContainer>
          <div>
            <Title>Category Breakdown</Title>
            <Subtitle>
              {competingCommunitiesCount
                ? `Rank shown is yours among ${competingCommunitiesCount} competing communities. Full analysis in AI Competitive Intelligence.`
                : 'Full analysis in AI Competitive Intelligence.'}
            </Subtitle>
          </div>
        </TitleGroup>
        <AICIButton data-test-id="category-breakdown-ai-ci-button" onClick={handleOpenAICompetitiveIntelligence}>
          <img src="/icons/ai-blob-white.svg" alt="" width={20} height={20} /> AI Competitive Intelligence
          <ChevronRight fill="var(--white)" width={20} height={20} />
        </AICIButton>
      </Header>
      <Body>
        <CategoryRankList categories={categories} />
        <CompetitiveAdvantagesImprovements strengths={strengths} limitations={limitations} />
      </Body>
    </Container>
  );
};

export default CategoryBreakdownCard;
